import { BY_REQUEST_PRICE_SLOT_LABEL } from '../constants/byRequestCopy';
import { isTimeUnit } from './pricingUnits';

/** Currency shown when a booking carries none. */
export const DEFAULT_DISPLAY_CURRENCY = 'USD';

export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
  options: { alwaysCents?: boolean } = {},
): string => {
  // Whole-dollar amounts drop the decimals ("$948"); fractional amounts keep
  // exact cents ("$414.75"). Checking the rounded cent remainder (rather than
  // `amount % 1`) avoids floating-point noise from upstream multiplier math
  // — e.g. a 1.1x surge on a $100 base (100 * 1.1) lands at
  // 110.00000000000001 in IEEE-754, which `% 1` would misread as fractional.
  // `options.alwaysCents` opts a caller out of that truncation entirely —
  // for surfaces (e.g. an exact-charge consent screen) where a whole-dollar
  // amount must still render with two decimal places.
  const showCents = options.alwaysCents || Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatPrice = (amount: number): string => {
  return formatCurrency(amount);
};

/** Below this dollar amount, {@link formatTilePrice} rounds up to the next
 *  whole dollar; at or above it, to the next coarse step (ADR-0094). */
export const TILE_PRICE_COARSE_STEP_THRESHOLD = 100;

/** The coarse rounding step (in whole dollars) applied at or above
 *  {@link TILE_PRICE_COARSE_STEP_THRESHOLD} (ADR-0094). */
export const TILE_PRICE_COARSE_STEP = 5;

/**
 * Format a browse-tile price for display, rounding UP so the tile never
 * quotes less than what checkout will actually charge (ADR-0094). Below
 * {@link TILE_PRICE_COARSE_STEP_THRESHOLD} rounds up to the next whole
 * dollar; at or above it, to the next {@link TILE_PRICE_COARSE_STEP}-dollar
 * step. Browse tiles only — detail, cart, and checkout surfaces must keep
 * rendering the exact charge via {@link formatCurrency} / {@link formatPrice}.
 */
export const formatTilePrice = (amount: number, currency: string = 'USD'): string => {
  // Whole-cents arithmetic (not float dollars) so the 100 * 1.1 →
  // 110.00000000000001 IEEE-754 noise that `formatCurrency` already guards
  // against can't push the coarse step one tier too high here either.
  const totalCents = Math.round(amount * 100);
  const thresholdCents = TILE_PRICE_COARSE_STEP_THRESHOLD * 100;
  const dollars =
    totalCents < thresholdCents
      ? Math.ceil(totalCents / 100)
      : Math.ceil(totalCents / (TILE_PRICE_COARSE_STEP * 100)) * TILE_PRICE_COARSE_STEP;
  return formatCurrency(dollars, currency);
};

/**
 * Format a cart or checkout line's total for display, rendering the shared
 * by-request price-slot copy in place of a price when the line has none yet
 * (its `totalPrice` is `null` — an admin quote settles the price later).
 * Reuses `BY_REQUEST_PRICE_SLOT_LABEL` — the existing "every price-less UI
 * slot" constant — rather than a second, near-duplicate string, so a future
 * copy edit lands in one place for every by-request price slot, cart lines
 * included. Delegates to `formatCurrency` for every priced line, so the two
 * money-render conventions (whole-dollar truncation, currency symbol) never
 * diverge between a priced and an unpriced line rendered side by side.
 */
export const formatCartLinePrice = (
  totalPrice: number | null,
  currency: string = 'USD',
): string => {
  if (totalPrice === null) return BY_REQUEST_PRICE_SLOT_LABEL;
  return formatCurrency(totalPrice, currency);
};

/**
 * Parse a `YYYY-MM-DD` string as **local** midnight.
 *
 * `new Date('2026-07-05')` parses a date-only string as UTC midnight, which
 * `toLocaleDateString` then renders as the *previous* day for any viewer west
 * of UTC. Building the Date from its parts pins it to local time so a stored
 * search date round-trips to the same calendar day it was picked on, in every
 * timezone. Returns `null` for a malformed string. Inverse of
 * {@link toIsoDateLocal}.
 */
export const parseIsoDateLocal = (iso: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
};

/**
 * Format a local `Date` (or epoch-ms timestamp) as `YYYY-MM-DD` from its
 * **local** calendar components. Inverse of {@link parseIsoDateLocal}.
 *
 * Using `toISOString().slice(0, 10)` here would shift the stored day for
 * viewers west of UTC — a local-midnight instant serialises to the next UTC
 * day — so we read local components instead to keep the round-trip stable.
 */
export const toIsoDateLocal = (date: Date | number): string => {
  const d = typeof date === 'number' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a `YYYY-MM-DD` date-only string for customer-facing display.
 *
 * Parses the date via {@link parseIsoDateLocal} (local midnight) then calls
 * `toLocaleDateString(locale, options)`. Using `new Date(iso)` directly on a
 * date-only string would parse it as UTC midnight, causing viewers west of UTC
 * to see the *previous* day — e.g. `'2026-07-05'` → `'Jul 4, 2026'` in LA.
 * This function pins the parse to local time so the stored calendar day is
 * always what the customer sees.
 *
 * Returns `null` when `iso` is not a well-formed `YYYY-MM-DD` string (so the
 * caller can gate the row render on a non-null return rather than surface a
 * raw error string).
 */
export const formatCalendarDate = (
  iso: string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = 'en-US',
): string | null => {
  const localDate = parseIsoDateLocal(iso);
  if (localDate === null) return null;
  return localDate.toLocaleDateString(locale, options);
};

/**
 * Format a `YYYY-MM-DD` stay date as "Tue, Oct 6", adding the year only when it
 * differs from `today`'s year. Returns `null` for a malformed string.
 */
export const formatStayDate = (iso: string, today: Date = new Date()): string | null => {
  const stayDate = parseIsoDateLocal(iso);
  if (stayDate === null) return null;
  const yearDiffers = stayDate.getFullYear() !== today.getFullYear();
  return formatCalendarDate(iso, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(yearDiffers ? { year: 'numeric' } : {}),
  });
};

/**
 * Derive a package's display check-out date from its check-in date and
 * duration in nights, as a `YYYY-MM-DD` string. A package cart line carries
 * only a check-in date; the server derives the real end date from the
 * package's own `duration_nights`, and this reproduces it purely for display
 * so the cart line and the checkout summary render the same range from one
 * source. Uses the local-midnight helpers so the derived day never shifts for
 * viewers west of UTC. Returns `undefined` when the check-in date is
 * absent/malformed or the duration is unknown (the caller then shows the
 * single check-in date, with no range).
 */
export const derivePackageCheckOutDate = (
  checkInDate?: string,
  durationNights?: number | null,
): string | undefined => {
  if (!checkInDate || durationNights == null) return undefined;
  const checkInLocal = parseIsoDateLocal(checkInDate);
  if (checkInLocal === null) return undefined;
  checkInLocal.setDate(checkInLocal.getDate() + durationNights);
  return toIsoDateLocal(checkInLocal);
};

/**
 * Format a service duration (in minutes) into a compact human label:
 *   45  → "45 min"
 *   120 → "2h"
 *   150 → "2h 30m"
 * Mirrors the inline formatting in `ServiceMainContent` (desktop).
 */
export const formatServiceDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours}h` : `${hours}h ${remainder}m`;
};

interface ServiceLengthRow {
  pricing_unit?: string | null;
  duration?: number | null;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
}

const MINUTES_PER_CLOCK_UNIT: Record<string, number> = { hour: 60, '30min': 30 };

const formatTimeUnitBounds = (unit: string, min: number | null, max: number | null): string => {
  const minutesPerUnit = MINUTES_PER_CLOCK_UNIT[unit];
  if (minutesPerUnit === undefined) {
    const days = (count: number) => (count === 1 ? 'day' : 'days');
    if (min !== null && max !== null) return `${min}–${max} ${days(max)}`;
    if (min !== null) return `${min} ${days(min)} minimum`;
    return `Up to ${max} ${days(max as number)}`;
  }
  const clock = (count: number) => formatServiceDuration(count * minutesPerUnit);
  if (min !== null && max !== null) {
    // "4–8h" when both ends are whole hours; otherwise each end in full.
    const bothWholeHours = (min * minutesPerUnit) % 60 === 0 && (max * minutesPerUnit) % 60 === 0;
    return bothWholeHours
      ? `${(min * minutesPerUnit) / 60}–${clock(max)}`
      : `${clock(min)}–${clock(max)}`;
  }
  if (min !== null) return `${clock(min)} minimum`;
  return `Up to ${clock(max as number)}`;
};

/**
 * The length a guest sees for a service or option. A time-priced row (hour,
 * 30 min, day) is booked for a guest-chosen length, so it shows its bookable
 * bounds ("4h minimum", "4–8h", "Up to 8h") and never its fixed duration;
 * any other row shows its duration. Empty when there is nothing to show.
 */
export const formatServiceLength = (row: ServiceLengthRow): string => {
  if (isTimeUnit(row.pricing_unit)) {
    const min = row.minimum_unit_count ?? null;
    const max = row.maximum_unit_count ?? null;
    if (min === null && max === null) return '';
    return formatTimeUnitBounds((row.pricing_unit ?? '').trim().toLowerCase(), min, max);
  }
  return row.duration ? formatServiceDuration(row.duration) : '';
};

export const getAbsoluteImageUrl = (relativeUrl: string | null | undefined): string | undefined => {
  if (!relativeUrl) return undefined;

  // If the URL is already absolute, return as is
  if (relativeUrl.startsWith('http')) {
    return relativeUrl;
  }

  // Convert relative URLs to absolute by prepending backend base URL
  const { API_BASE_URL } = require('../config/api');
  return `${API_BASE_URL}${relativeUrl}`;
};

// ---------------------------------------------------------------------------
// PM policy disclosure — confirmation-page copy
// ---------------------------------------------------------------------------
//
// The customer-facing copy mapped from the NextPax
// `keyCollectionMethod` / `damageDepositRequirement` enums (see
// `~/shared/docs/nextpax_api/nextpax-distribution-api.yaml` line ~3267 and
// ~3230). Lives here so `BookingConfirmationPage` and
// `MultiBookingConfirmationPage` render identical phrasing — divergence
// between the two confirmation surfaces is exactly the kind of trap a
// future copy edit walks into when the mapping is duplicated inline.

const KEY_COLLECTION_METHOD_COPY: Record<string, string> = {
  // The two officially documented synonyms map to the same copy — NextPax
  // sandbox publishes `at_reception`; older PM tooling sometimes sends
  // `pick_up_keys_at_reception`. Both must render the same row.
  at_reception: 'Keys at reception on arrival',
  pick_up_keys_at_reception: 'Keys at reception on arrival',
  someone_will_meet: 'Someone will meet you at the property',
  door_code: 'Door code — access details will arrive before check-in',
  lock_box: 'Lock box — access details will arrive 24h before check-in',
  will_send_instructions: "We'll send check-in instructions before arrival",
  contact_us: 'Contact your host to arrange key collection',
  secret_location: 'Keys at a secret location — instructions will be shared before arrival',
  other: 'Your host will share key collection details before arrival',
};

/**
 * Map a NextPax `keyCollectionMethod` enum value to customer-facing copy
 * for the post-booking confirmation surface. Returns `null` when the
 * field is null/undefined or when the enum value is unrecognised — the
 * caller MUST gate the row render on a non-null return so an
 * unknown-to-Belhop value collapses silently instead of leaking the raw
 * enum into the UI.
 */
export const formatKeyCollectionMethod = (method: string | null | undefined): string | null => {
  if (method == null) return null;
  return KEY_COLLECTION_METHOD_COPY[method] ?? null;
};

const DAMAGE_DEPOSIT_REMINDER_COPY: Record<string, string> = {
  at_booking_time: 'A damage deposit applies for this booking — collected at booking.',
  before_arrival: 'A damage deposit applies for this booking — collected before your arrival.',
  at_arrival: 'A damage deposit applies for this booking — your host will collect it on arrival.',
};

/**
 * Map a NextPax `damageDepositRequirement` enum value to customer-facing
 * reminder copy. Returns `null` for the `no_deposit` value (the common
 * case — no reminder needed) and for unknown values, so the caller's
 * gate-on-non-null pattern hides the row silently. The `damageDepositAmount`
 * is not yet surfaced; the reminder is qualitative until a real PM forces
 * it.
 */
export const formatDepositReminder = (requirement: string | null | undefined): string | null => {
  if (requirement == null || requirement === 'no_deposit') return null;
  return DAMAGE_DEPOSIT_REMINDER_COPY[requirement] ?? null;
};

// ---------------------------------------------------------------------------
// Booking guest split — granular vs legacy display
// ---------------------------------------------------------------------------
//
// The Booking response carries a granular `adults` + `children` split.
// The customer-facing detail and confirmation pages render "3 adults,
// 1 child" rather than collapsing both into "4 guests".
// Legacy Booking rows that pre-date the new columns carry `adults = null`;
// the helper falls back to the legacy "{guest_count} guests" string on
// the null path so no row backfill is required. Single source of truth so
// the detail page and the confirmation page stay phrasing-identical — a
// divergence between the two surfaces is exactly the trap a future copy
// edit walks into when the format is duplicated inline.

/**
 * Compose the customer-facing guest-split string for a booking.
 *
 * @param adults — granular adults count when present; `null` / `undefined`
 *   for legacy rows that pre-date the column.
 * @param children — granular children count; defaults to `0` on absent.
 * @param guestCount — the legacy single-count fallback rendered when
 *   `adults` is null/undefined.
 */
export const formatGuestSplit = (
  adults: number | null | undefined,
  children: number | null | undefined,
  guestCount: number,
): string => {
  if (adults == null) {
    return `${guestCount} guest${guestCount === 1 ? '' : 's'}`;
  }
  const adultLabel = `${adults} adult${adults === 1 ? '' : 's'}`;
  const childCount = children ?? 0;
  if (childCount === 0) return adultLabel;
  const childLabel = `${childCount} ${childCount === 1 ? 'child' : 'children'}`;
  return `${adultLabel}, ${childLabel}`;
};

/**
 * Normalise a stored check-in / check-out time to a consistent 12-hour
 * `"h:MM AM/PM"` string.
 *
 * NextPax syncs 24-hour times (`checkinFrom` "14:00"), while native
 * listings store 12-hour strings ("3:00 PM"), so the raw column would
 * render inconsistently across the site. This normalises both shapes at
 * the presentation layer — the DB / wire value stays the source of truth.
 *
 * Returns `null` for null/empty input so the caller omits the block
 * rather than faking a value. Input that matches neither a 24-hour
 * `HH:MM` nor a 12-hour `h:MM AM/PM` shape (or carries an out-of-range
 * hour/minute) is returned as the trimmed raw string — whatever the PM
 * provided is shown, never hidden.
 */
export const formatCheckTime = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  // Already 12-hour (e.g. "3:00 PM", "3:00pm") — canonicalise the meridiem
  // to a single uppercase suffix and keep the clock time.
  const twelveHour = trimmed.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (twelveHour) {
    const [, hour, minute, meridiem] = twelveHour;
    if (Number(hour) >= 1 && Number(hour) <= 12 && Number(minute) <= 59) {
      return `${Number(hour)}:${minute} ${meridiem.toUpperCase()}`;
    }
  }

  // 24-hour "HH:MM" (NextPax `checkinFrom` / `checkoutUntil`). Tolerates a
  // trailing ":SS" some feeds include.
  const twentyFourHour = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (twentyFourHour) {
    const hour24 = Number(twentyFourHour[1]);
    const minute = twentyFourHour[2];
    if (hour24 <= 23 && Number(minute) <= 59) {
      const meridiem = hour24 < 12 ? 'AM' : 'PM';
      const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
      return `${hour12}:${minute} ${meridiem}`;
    }
  }

  // Unrecognised shape — surface the raw value rather than hide it.
  return trimmed;
};
